import React, { useMemo, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { snakecaseToTitlecase } from "@tomocredit/common-fe/utils";

import {
  withStyles,
  withMobile,
  withOverlay,
  withModal,
  withBreakpoint,
} from "components/common";
import InfiniteScroll from "react-infinite-scroller";
import Table from "components/common/Table";
import { useApolloClient } from "@apollo/react-hooks";
import { formatCurrency, unixToShortDate } from "utils";
import sortDate from "utils/sortDate";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const [red, green, blue, gray] = ["#FF8B89", "#60C8B1", "#289EFC", "#cbd5e0"];

const descriptionText = (data) => {
  let desc = data.merchant;
  if (data.type === 'dispute.won') {
    desc += ' (Dispute Refund)';
  }
  return desc;
};

const computeColumns = (isBelowSm) =>
  isBelowSm
    ? [
      {
        Header: "Description",
        unsortable: true,
        accessor: (data) => (
          <div className="flex items-center leading-tight">
            <div
              className={`flex flex-col items-center mr-2 text-sm md:text-base md:mr-0 border-r border-gray-200 pr-2 
                ${data.returned_at ? "" : "text-gray-500"}`}
            >
              <div>{months[new Date(data.date).getMonth()]}</div>
              <div>{new Date(data.date).getDate()}</div>
            </div>
            <div className="flex-1 ml-1 md:ml-0">
              <div>
                {descriptionText(data)}
              </div>
              <div
                className={`${data.returned_at ? "" : "text-gray-500"} 
                  text-sm md:text-base`}
              >
                {snakecaseToTitlecase(data.category ||
                  (`${data.amount || ""}`.startsWith("-") ? "credit" : ""))}
              </div>
            </div>
          </div>
        ),
      },
      {
        Header: "Amount",
        unsortable: true,
        style: { paddingTop: 0, paddingBottom: 0 }, // avoid expanding the row when this cell is taller
        accessor: (data) => {
          const t = (data.type || "").toLowerCase();
          const c = (data.category || "").toLowerCase();
          const a = `${data.amount || ""}`;
          const s = `${data.status || ""}`.toLowerCase();
          return (
            <div
              style={{
                color: (() => {
                  if (data.amount > 0) {
                    return green;
                  }
                  if (data.type === 'dispute.won') {
                    return gray;
                  }
                  return "";
                })(),
              }}
            >
              {`${data.returned_at ? "Failed/Returned " : ""}${
                data.amount ? formatCurrency(data.amount.toFixed(2)) : ""
              }`}
              {(() => {
                if (s === "pending") {
                  return (
                    <div className="font-normal text-gray-500 text-xxs">
                      pending
                    </div>
                  );
                }
                if (s === "canceled") {
                  return (
                    <div
                      style={{ color: red }}
                      className="font-normal text-xxs"
                    >
                      canceled
                    </div>
                  );
                }
                return "";
              })()}
            </div>
          );
        },
      },
    ]
  : [
      {
        Header: "Date",
        unsortable: true,
        style: { width: "200px" },
        accessor: (data) =>
          unixToShortDate(data.date)
      },
      {
        Header: "Description",
        unsortable: true,
        accessor: (data) => (
          <div>
            {data.disputed ? (
              <i className="fas fa-exclamation-triangle text-red-500 mr-2 text-sm" />
            ) : (
              <></>
            )}
            {descriptionText(data)}
          </div>
        ),
      },
      {
        Header: "Category",
        unsortable: true,
        accessor: (data) => {
          if (`${data.amount || ""}`.startsWith("-"))
            return snakecaseToTitlecase(data.category) || "Credit";
          return snakecaseToTitlecase(data.category) || "";
        },
      },
      {
        Header: "Amount",
        unsortable: true,
        style: { paddingTop: 0, paddingBottom: 0 }, // avoid expanding the row when this cell is taller
        accessor: (data) => {
          // If we want to make payment amounts negative, add the below to the string:
          // ${(data.description || "").toLowerCase() === "payment" ? "-" : "" }
          return `${
            data.amount ? formatCurrency(data.amount.toFixed(2)) : ""
          }`;
        },
        Cell: (data) => {
          const t = (data.row.original.type || "").toLowerCase();
          const c = (data.row.original.category || "").toLowerCase();
          const a = `${data.row.original.amount || ""}`;
          const s = `${data.row.original.status || ""}`.toLowerCase();
          return (
            <div>
              <div
                className="flex justify-between"
                style={{
                  color: (() => {
                    if (a > 0) {
                      return green;
                    }
                    return "";
                  })(),
                }}
              >
                <span>{`${
                  data.row.original.returned_at ? "Failed/Returned " : ""
                }`}</span>
                <span>{data.row.values.Amount}</span>
              </div>
              {(() => {
                if (s === "pending") {
                  return (
                    <div className="font-normal text-gray-500 text-xxs">
                      pending
                    </div>
                  );
                }
                if (s === "canceled") {
                  return (
                    <div
                      style={{ color: red }}
                      className="font-normal text-xxs"
                    >
                      canceled
                    </div>
                  );
                }
                return "";
              })()}
            </div>
          );
        },
      },
    ];

function showEmpty(activityLength) {
  if (activityLength === 0) {
    return (
      <p>You have no account activity yet. Start using your card today!</p>
    );
  }
}

const getRowStyle = (row) => {
  return {
    backgroundColor: row.original.returned_at ? red : "",
  };
};

function showPending(
  goodPendingTransactions,
  goodSettledTransactions,
  columns,
  isMobile,
  openOverlay,
  openModal,
) {
  const totalPendingActivityCount = goodPendingTransactions.length;
  const totalPendingActivityVolume = goodPendingTransactions.reduce(
    (sum, activity) => Number(activity.amount) + Number(sum),
    0,
  );

  const PendingTableFooter = () => {
    return (
      <tr className="bg-green-secondary">
        <td colSpan={columns.length - 1} className="text-right">
          Total Pending Transactions ({totalPendingActivityCount})
        </td>
        <td className="font-medium">
          {formatCurrency(totalPendingActivityVolume) || "$0.00"}
        </td>
      </tr>
    );
  };

  if (goodPendingTransactions.length > 0) {
    return (
      <>
        <h3 className="font-title mb-2 text-xl">Pending Transactions</h3>
        <Table
          id="pending-transactions-table"
          data={sortDate(goodPendingTransactions)}
          columns={columns}
          getRowStyle={getRowStyle}
          // onRowClick={(e, row) => {}} TODO
          TableFooterComponent={PendingTableFooter}
        />

        {goodSettledTransactions.length > 0 ? <br /> : null}
      </>
    );
  }
}

function showSettled(
  goodSettledTransactions,
  columns,
  isMobile,
  openOverlay,
  openModal,
) {
  if (goodSettledTransactions.length > 0) {
    return (
      <>
        <h3 className="font-title mb-2 text-xl">Settled Transactions</h3>
        <Table
          id="settled-transactions-table"
          data={sortDate(goodSettledTransactions)}
          columns={columns}
          getRowStyle={getRowStyle}
          // onRowClick={ (e, row) => {} } TODO
        />
      </>
    );
  }
}

const TransactionsTable = ({
  mobileContext: { isMobile },
  overlayContext: { openOverlay },
  modalContext: { openModal },
  breakpointContext: { isBelow },
  pending = [],
  settled = [],
  nextToken,
  activityQuery,
}) => {
  const [activityLength, setActivityLength] = useState(pending.length + settled.length); // used to determine when to show empty list UI state
  const columns = useMemo(() => computeColumns(isBelow("sm")), [isBelow]);
  const client = useApolloClient();
  const [goodPendingTransactions, setGoodPendingTransactions] = useState(pending);
  const [goodSettledTransactions, setGoodSettledTranasctions] = useState(settled);
  const [mostRecentToken, setMostRecentToken] = useState(nextToken);
  const [loading, setLoading] = useState(false);

  const loadMore = () => {
    console.log('trying to load more');
    if (loading || !mostRecentToken) {
      return;
    }
    setLoading(true);
    client
      .query({
        query: activityQuery,
        fetchPolicy: "network-only",
        variables: {
          type: 'settled',
          nextToken: mostRecentToken,
        },
      })
      .then(({ data }) => {
        const {
          data: { transactions: a = [], nextToken: nextPageToken },
        } = data;
        setLoading(false);
        setMostRecentToken(nextPageToken);
        if (a.length) {
          setActivityLength(activityLength + a.length); // only set here as length is non-filtered by user's search keywords

          setGoodSettledTranasctions(
            goodSettledTransactions.concat(
              ...a,
            ),
          );
        }
      });
  };

  const loader = () => <div className="loader m-auto h-16 w-16" key={0} />;

  // checks if bottom is in view.
  return (
    <InfiniteScroll
      loadMore={loadMore}
      hasMore={!!mostRecentToken}
      loader={loader()}
    >
      <div className="my-6">
        {showEmpty(activityLength)}
        {showPending(
          goodPendingTransactions,
          goodSettledTransactions,
          columns,
          isMobile,
          openOverlay,
          openModal,
        )}
        {showSettled(
          goodSettledTransactions,
          columns,
          isMobile,
          openOverlay,
          openModal,
        )}
      </div>
    </InfiniteScroll>
  );
};

export default compose(
  connect((state) => ({
    userId: state.user.id,
  })),
  withMobile,
  withStyles,
  withOverlay,
  withModal,
  withBreakpoint,
)(TransactionsTable);
